import {
  Button,
  CurrencyInput,
  Drawer,
  Hairline,
  Label,
  Loading,
  Radio,
  Select,
} from "@components";

import styles from "../../../styles/service/service.module.scss";
import dayjs from "dayjs";
import { OrderStorage } from "storage/OrderStorage";
import { If } from "shared/components/control-statements";
import CartItems from "./CartItems";
import { useCallback, useEffect } from "react";
import { EnvironmentsAPI } from "providers/EnvironmentsAPI";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";
import { Method, PaymentMethod } from "shared/utils/Payment";
import { PaymentGroup } from "shared/utils/PaymentOnDelivery/PaymentGroup";
import { useState } from "react";
import { CatalogHelpers } from "shared/tools/CatalogHelpers";
import { OMath } from "shared/utils/OMath";
import { DeliveryType } from "@olga-food/schemas/lib/classes/schemas/order/delivery/Delivery";
import { Currency } from "shared/utils/Currency";
import { Phone } from "shared/utils/Phone";
import { formatAddress } from "./Service";
import { ClubAPI } from "providers/ClubAPI";
import { InputSearch } from "shared/components/InputSearch";
import { Benefit, GIFT_TYPE } from "shared/core/Benefit";

import classNames from "classnames/bind";
import { Add } from "shared/components/Add";
import { AddCoupon } from "./AddCoupon";
import { OrderAPI } from "providers/OrderAPI";
import { CheckIcon, SuccessIcon } from "shared/components/Icons";
import { useNavigate } from "react-router";
import InternalBenefitAPI from "providers/InternalBenefitAPI";
const css = classNames.bind(styles);

const defaultPaymentGroups = {
  CREDIT: [
    { name: "Visa", alias: "VISA", active: true, index: 0 },
    { name: "Mastercard", alias: "MASTERCARD", active: true, index: 1 },
    { name: "Hiper", alias: "HIPER", active: true, index: 2 },
    { name: "Elo", alias: "ELO", active: false, index: 3 },
    {
      name: "American Express",
      alias: "AMERICAN_EXPRESS",
      active: false,
      index: 4,
    },
  ],
  DEBIT: [
    { name: "Visa", alias: "VISA", active: true, index: 0 },
    { name: "Mastercard", alias: "MASTERCARD", active: true, index: 1 },
    { name: "Hiper", alias: "HIPER", active: true, index: 2 },
    { name: "Elo", alias: "ELO", active: false, index: 3 },
  ],
  MEAL_VOUCHER: [
    { name: "Alelo", alias: "ALELO_REFEICAO", active: false, index: 0 },
    { name: "VR", alias: "VR_REFEICAO", active: false, index: 1 },
    { name: "Sodexo", alias: "SODEXO_REFEICAO", active: false, index: 2 },
  ],
  FOOD_VOUCHER: [
    { name: "Alelo", alias: "ALELO_ALIMENTACAO", active: false, index: 0 },
    { name: "VR", alias: "VR_ALIMENTACAO", active: false, index: 1 },
    {
      name: "Sodexo",
      alias: "SODEXO_ALIMENTACAO",
      active: false,
      index: 2,
    },
  ],
  MONEY: [{ name: "Dinheiro", alias: "DINHEIRO", active: false, index: 0 }],
};

const internalBenefitAPI = new InternalBenefitAPI();

const ConfirmOrder = ({
  open,
  onClose,
  selectedCustomer,
  order,
  onChangeCustomerId,
  maxDesk,
  minDesk,
}) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [changeAmount, setChangeAmount] = useState(0);
  const [openChangeAmount, setOpenChangeAmount] = useState(false);
  const [openCoupons, setOpenCoupons] = useState(false);
  const [searchCoupon, setSearchCoupon] = useState("");
  const [selectedCoupon, setSelectedCoupon] = useState(OrderStorage.getGift());
  const [cashback, setCashback] = useState(0);
  const [coupons, setCoupons] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [openAddCoupon, setOpenAddCoupon] = useState(false);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openOrderSuccess, setOpenOrderSuccess] = useState(false);
  const [customerBlocked, setCustomerBlocked] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const cartItems = OrderStorage.getOrder()?.cart?.catalog?.items;
    const hasItems = cartItems?.length > 0;
    if (!hasItems) onClose();
  }, [order, onClose, open]);

  useEffect(() => {
    const refreshMethods = async ({ payment_methods }) => {
      const pgCredit = new PaymentGroup(
        "Crédito",
        "CREDIT",
        PaymentMethod.CREDIT,
        defaultPaymentGroups.CREDIT,
        payment_methods || [],
      );
      const pgDebit = new PaymentGroup(
        "Débito",
        "DEBIT",
        PaymentMethod.DEBIT,
        defaultPaymentGroups.DEBIT,
        payment_methods || [],
      );
      const pgDinner = new PaymentGroup(
        "Voucher Refeição",
        "MEAL_VOUCHER",
        PaymentMethod.MEAL | PaymentMethod.VOUCHER,
        defaultPaymentGroups.MEAL_VOUCHER,
        payment_methods || [],
      );
      const pgFood = new PaymentGroup(
        "Voucher Alimentação",
        "FOOD_VOUCHER",
        PaymentMethod.FOOD | PaymentMethod.VOUCHER,
        defaultPaymentGroups.FOOD_VOUCHER,
        payment_methods || [],
      );
      const pgMoney = new PaymentGroup(
        "Dinheiro",
        "MONEY",
        PaymentMethod.MONEY,
        defaultPaymentGroups.MONEY,
        payment_methods || [],
      );
      return [pgCredit, pgDebit, pgDinner, pgFood, pgMoney];
    };

    const loadPaymentMethods = async () => {
      const response = await EnvironmentsAPI.getConfigurationsPaymentMethods(
        EnvironmentStorage.getSelectedEnvironment(),
      );

      const configurations = response || {};
      const groups = await refreshMethods(configurations || {});

      const paymentMethods = groups.map((group) => {
        return {
          name: group.name,
          flags: group.flags,
        };
      });

      const activeMethods = paymentMethods.flatMap((group) => {
        const activeFlags = group.flags.filter((flag) => flag.active);
        return activeFlags.map((flag) => {
          const isMoney = flag.alias === Method.DINHEIRO?.alias;

          return {
            name: isMoney ? group.name : `${group.name} - ${flag.name}`,
            alias: flag.alias,
            type: flag.type,
            active: flag.active,
          };
        });
      });

      setPaymentMethods(activeMethods);
    };

    loadPaymentMethods();
  }, []);

  // useEffect(() => {
  //   if (!selectedPayment && paymentMethods.length > 0)
  //     setSelectedPayment(paymentMethods[0]);
  // }, [paymentMethods, selectedPayment]);

  const preparePayloadOrder = () => {
    const payloadOrder = OrderStorage.getOrder();
    const customer = {};
    delete customer.password;

    if (customer.extras) {
      delete customer.extras.addresses;
    }

    payloadOrder.cart.catalog = CatalogHelpers.clearUnselectedItems(
      payloadOrder.cart.catalog,
    );

    if (payloadOrder.delivery) {
      payloadOrder.delivery.total = OMath.sub(
        order.delivery?.total || 0,
        order.delivery?.discount || 0,
      );
    }

    return payloadOrder;
  };

  const preparePayer = () => {
    const customer = selectedCustomer || {};

    const payer = {
      amount: order.total,
      user: customer,
    };

    const setOfflinePayer = () => {
      payer.method = selectedPayment.alias;
      payer.paymentMethod = selectedPayment.type;
      payer.changeFor = changeAmount;
    };

    setOfflinePayer();

    return payer;
  };

  const onCheckout = async () => {
    setLoading(true);

    const payloadOrder = preparePayloadOrder();
    const payer = preparePayer();

    payloadOrder.payment = {
      total: payloadOrder.total,
      payers: [payer],
    };

    if (payloadOrder.gift && !payloadOrder.gift.type) {
      payloadOrder.gift = null;
    }

    if (payloadOrder.delivery.type !== DeliveryType.DELIVER_ON_ADDRESS) {
      payloadOrder.delivery = {
        ...payloadOrder.delivery,
        deliveryStrip: {
          ...payloadOrder.delivery.deliveryStrip,
          minDesk,
          maxDesk,
        },
      };
    }

    payloadOrder.origin = 6;

    console.log(payloadOrder);

    const order = await OrderAPI.postOrders(payloadOrder);

    console.log(order);

    const code = order.getCode(500);
    if (code !== 200) {
      alert({ text: order.getErrors()[0] });
      setLoading(false);
      return;
    }

    setOpenOrderSuccess(true);
    OrderStorage.initOrder();
    onClose();

    setLoading(false);
  };

  const getCustomerInfos = useCallback(async () => {
    setLoadingCoupons(true);

    try {
      const response = await ClubAPI.getCustomerInfos(selectedCustomer);

      setCoupons(response?.benefits?.coupons || []);
      setCashback(response?.benefits?.cashback?.total || 0);
      const customerId = response?.id;

      setCustomerId(customerId);
      onChangeCustomerId(customerId);

      const blockResponse =
        await internalBenefitAPI.getCustomerBlock(customerId);
      const block = blockResponse.getData(null);
      setCustomerBlocked(!!block);
    } catch (error) {
      console.error("Erro ao buscar informações do cliente:", error);
      setCoupons([]);
      setCashback(0);
      setCustomerId(null);
    } finally {
      setLoadingCoupons(false);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    if (!selectedCustomer) return;

    getCustomerInfos();
  }, [selectedCustomer, getCustomerInfos]);

  useEffect(() => {
    const getDeliveryFree = async () => {
      const freeDelivery = await EnvironmentsAPI.getConfigurationsByAlias(
        EnvironmentStorage.getSelectedEnvironment(),
        "free_delivery",
      );

      EnvironmentStorage.setFreeDelivery(freeDelivery);
    };

    getDeliveryFree();
  }, []);

  const filterCoupons = () => {
    if (!searchCoupon) return coupons;

    const normalized = searchCoupon.toLowerCase();

    return coupons.filter((item) => {
      const code = String(item.code).toLowerCase();
      const pin = String(item.pin).toLowerCase();

      const match = code.includes(normalized) || pin.includes(normalized);
      const codePin = (code + pin).toLowerCase().includes(normalized);

      return match || codePin;
    });
  };

  const filteredCoupons = filterCoupons();

  const orderGift = OrderStorage.getGift();
  const orderDelivery = OrderStorage.getDelivery();

  const isFreeDelivery =
    (orderGift && orderGift.type === "FREE_DELIVERY") ||
    order?.delivery?.total === 0 ||
    !order?.delivery?.total;

  const programByPoint =
    localStorage.getItem("cashbackProgramType") === "POINT";

  const getGiftType = (gift) => {
    if ((gift.type & GIFT_TYPE.CASHBACK_USE) === GIFT_TYPE.CASHBACK_USE)
      return "Desconto (Cashback)";

    if ((gift.type & GIFT_TYPE.CASHBACK_EARN) === GIFT_TYPE.CASHBACK_EARN) {
      if (programByPoint) return `Pontos concebidos`;
      return `Cashback concebido`;
    }

    return `Desconto (${gift.code?.split("-")?.[0]})`;
  };

  return (
    <>
      <Drawer title="Fechar pedido" visible={open} onClose={() => onClose()}>
        <div className={styles.confirmOrder}>
          <div className={styles.customerContainer}>
            <div className="d-flex align-center gap-15">
              <svg
                width="40"
                height="40"
                viewBox="0 0 60 60"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 30C1 13.9837 13.9837 1 30 1C46.0163 1 59 13.9837 59 30C59 46.0163 46.0163 59 30 59C13.9837 59 1 46.0163 1 30Z"
                  stroke="#333333"
                  strokeWidth="2"
                />
                <path
                  d="M32.4669 26.6412C32.4669 26.3103 32.403 25.9826 32.2789 25.6769C32.1549 25.3711 31.9731 25.0933 31.7438 24.8593C31.5146 24.6253 31.2425 24.4397 30.943 24.3131C30.6435 24.1864 30.3225 24.1212 29.9983 24.1212C29.6741 24.1212 29.3531 24.1864 29.0536 24.3131C28.7541 24.4397 28.482 24.6253 28.2527 24.8593C28.0235 25.0933 27.8417 25.3711 27.7176 25.6769C27.5936 25.9826 27.5297 26.3103 27.5297 26.6412C27.5297 26.9722 27.5936 27.2999 27.7176 27.6056C27.8417 27.9114 28.0235 28.1892 28.2527 28.4232C28.482 28.6572 28.7541 28.8428 29.0536 28.9694C29.3531 29.0961 29.6741 29.1612 29.9983 29.1612C30.3225 29.1612 30.6435 29.0961 30.943 28.9694C31.2425 28.8428 31.5146 28.6572 31.7438 28.4232C31.9731 28.1892 32.1549 27.9114 32.2789 27.6056C32.403 27.2999 32.4669 26.9722 32.4669 26.6412ZM26.7069 26.6412C26.7069 25.7501 27.0536 24.8955 27.6709 24.2654C28.2882 23.6352 29.1253 23.2812 29.9983 23.2812C30.8712 23.2812 31.7084 23.6352 32.3257 24.2654C32.9429 24.8955 33.2897 25.7501 33.2897 26.6412C33.2897 27.5324 32.9429 28.387 32.3257 29.0171C31.7084 29.6473 30.8712 30.0012 29.9983 30.0012C29.1253 30.0012 28.2882 29.6473 27.6709 29.0171C27.0536 28.387 26.7069 27.5324 26.7069 26.6412ZM25.0611 35.8812H34.9354C34.9046 33.7891 33.2331 32.1012 31.1734 32.1012H28.8231C26.766 32.1012 25.0946 33.7891 25.0611 35.8812ZM24.2383 35.9416C24.2383 33.356 26.2903 31.2612 28.8231 31.2612H31.1734C33.7063 31.2612 35.7583 33.356 35.7583 35.9416C35.7583 36.3721 35.4163 36.7212 34.9946 36.7212H25.002C24.5803 36.7212 24.2383 36.3721 24.2383 35.9416Z"
                  fill="#6F767E"
                />
              </svg>

              <div className="d-flex flex-column">
                <p className={styles.label}>NOME DO CLIENTE</p>
                <p className={styles.name}>{selectedCustomer?.name}</p>
              </div>
            </div>

            <div className={styles.divider} />

            <div className="d-flex flex-column gap-10">
              <p className={styles.phone}>
                <i className="fal fa-phone" />
                <span>{Phone.mask(selectedCustomer?.phone || "")}</span>
              </p>
              <p className={styles.time}>
                <i className="fal fa-clock" />
                <span>
                  {dayjs(selectedCustomer?.createdAt).format(
                    "DD/MM/YYYY, HH:mm",
                  )}
                </span>
              </p>
            </div>
          </div>

          <Hairline className="mt-20 mb-20" />

          <div className={styles.addressContainer} onClick={onClose}>
            <p className={styles.label}>Endereço</p>
            <div className={styles.address}>
              <div className="d-flex align-center w-100 gap-10 justify-between">
                <div className="d-flex align-center gap-10">
                  <i className="fal fa-map-marker-alt" />
                  <p className={styles.addressText}>
                    <If
                      cond={
                        orderDelivery?.type === DeliveryType.DELIVER_ON_ADDRESS
                      }
                    >
                      {formatAddress(orderDelivery?.address)}
                    </If>
                    <If
                      cond={orderDelivery?.type === DeliveryType.GET_ON_PLACE}
                    >
                      Retirar na loja física
                    </If>
                  </p>
                </div>

                <i className="fal fa-pen" />
              </div>

              <If
                cond={orderDelivery?.type === DeliveryType.DELIVER_ON_ADDRESS}
              >
                <div className="d-flex align-center gap-5 mt-10">
                  <div className={styles.distance}>
                    <span>{orderDelivery?.distance?.toFixed(2)} KM</span>
                  </div>
                  <div className={styles.deliveryPrice}>
                    <span>{Currency.formatCurrency(orderDelivery?.total)}</span>
                  </div>
                </div>
              </If>
            </div>
          </div>

          <Hairline className="mt-20 mb-20" />

          <div className={styles.itemsContainer}>
            <p className={styles.label}>Pedido</p>

            <If cond={order?.observation}>
              <div className={styles.observation}>
                <div className="d-flex flex-column">
                  <div className="d-flex align-center gap-10">
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.6 9.1H1.4C1.01281 9.1 0.7 8.78719 0.7 8.4V1.4C0.7 1.01281 1.01281 0.7 1.4 0.7H8.4C8.78719 0.7 9.1 1.01281 9.1 1.4V5.6H7C6.22781 5.6 5.6 6.22781 5.6 7V9.1ZM8.80906 6.3L6.3 8.80906V7C6.3 6.61281 6.61281 6.3 7 6.3H8.80906ZM0 8.4C0 9.17219 0.627813 9.8 1.4 9.8H5.72031C6.09219 9.8 6.44875 9.65344 6.71125 9.39094L9.39094 6.70906C9.65344 6.44656 9.8 6.09 9.8 5.71812V1.4C9.8 0.627813 9.17219 0 8.4 0H1.4C0.627813 0 0 0.627813 0 1.4V8.4Z"
                        fill="#6161FF"
                      />
                    </svg>

                    <p className={styles.label}>OBSERVAÇÃO</p>
                  </div>
                  <p className={styles.observationText}>
                    {order?.observation || "Nenhuma observação"}
                  </p>
                </div>
              </div>
            </If>

            <CartItems isConfirmOrder />
          </div>

          <Hairline className="mt-20 mb-20" />

          <div className={styles.couponContainer}>
            <p className={styles.label}>Cupom</p>
            <If cond={!orderGift}>
              <div
                className={styles.selectCoupon}
                onClick={() => setOpenCoupons(true)}
              >
                <div className="d-flex align-center gap-15">
                  <svg
                    width="34"
                    height="34"
                    viewBox="0 0 34 34"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0 16C0 8.45753 0 4.68629 2.34315 2.34315C4.68629 0 8.45753 0 16 0H18C25.5425 0 29.3137 0 31.6569 2.34315C34 4.68629 34 8.45753 34 16V18C34 25.5425 34 29.3137 31.6569 31.6569C29.3137 34 25.5425 34 18 34H16C8.45753 34 4.68629 34 2.34315 31.6569C0 29.3137 0 25.5425 0 18V16Z"
                      fill="#292929"
                    />
                    <path
                      d="M10.0212 16.1067C9.9258 15.7505 10.1508 15.3818 10.5248 15.2816L21.3432 12.3828C21.7172 12.2826 22.0965 12.4894 22.1919 12.8456L22.4594 13.8437C21.728 14.1993 21.3211 15.005 21.5298 15.7838C21.7385 16.5625 22.4937 17.0568 23.3048 16.9991L23.5723 17.9972C23.6677 18.3534 23.4427 18.7221 23.0687 18.8223L12.2503 21.7211C11.8763 21.8213 11.497 21.6145 11.4016 21.2583L10.0212 16.1067ZM10.3523 14.6377C9.60642 14.8375 9.15475 15.5775 9.34509 16.2879L10.7255 21.4395C10.9158 22.1499 11.677 22.5649 12.4228 22.3651L23.2412 19.4663C23.9871 19.2664 24.4388 18.5264 24.2484 17.816L23.8904 16.4798C23.8473 16.3189 23.648 16.2515 23.479 16.2967C22.919 16.4468 22.3488 16.1359 22.2059 15.6026C22.0631 15.0693 22.4014 14.5149 22.9613 14.3649C23.1304 14.3196 23.2692 14.1616 23.2261 14.0006L22.8681 12.6644C22.6777 11.9541 21.9165 11.539 21.1707 11.7389L10.3523 14.6377ZM19.6599 14.2143C19.637 14.1289 19.5794 14.0565 19.4998 14.0131C19.4203 13.9697 19.3252 13.9588 19.2355 13.9829C19.1458 14.0069 19.0689 14.0639 19.0217 14.1412C18.9745 14.2186 18.9608 14.31 18.9837 14.3954C19.0066 14.4808 19.0642 14.5532 19.1437 14.5966C19.2233 14.64 19.3184 14.6508 19.4081 14.6268C19.4977 14.6028 19.5746 14.5458 19.6218 14.4685C19.6691 14.3911 19.6827 14.2996 19.6599 14.2143ZM20.005 15.5022C19.9821 15.4168 19.9245 15.3444 19.8449 15.301C19.7653 15.2576 19.6703 15.2467 19.5806 15.2708C19.4909 15.2948 19.414 15.3518 19.3668 15.4291C19.3196 15.5065 19.3059 15.5979 19.3288 15.6833C19.3517 15.7687 19.4092 15.8411 19.4888 15.8845C19.5684 15.9279 19.6635 15.9387 19.7531 15.9147C19.8428 15.8907 19.9197 15.8337 19.9669 15.7564C20.0142 15.679 20.0278 15.5875 20.005 15.5022ZM20.0982 17.2026C20.1879 17.1786 20.2648 17.1216 20.312 17.0443C20.3592 16.9669 20.3729 16.8754 20.35 16.7901C20.3272 16.7047 20.2696 16.6323 20.19 16.5889C20.1104 16.5455 20.0154 16.5346 19.9257 16.5587C19.836 16.5827 19.7591 16.6397 19.7119 16.717C19.6647 16.7944 19.651 16.8858 19.6739 16.9712C19.6968 17.0566 19.7543 17.129 19.8339 17.1724C19.9135 17.2158 20.0086 17.2266 20.0982 17.2026ZM20.6951 18.078C20.6723 17.9926 20.6147 17.9202 20.5351 17.8768C20.4555 17.8334 20.3605 17.8225 20.2708 17.8466C20.1811 17.8706 20.1042 17.9276 20.057 18.0049C20.0098 18.0823 19.9961 18.1737 20.019 18.2591C20.0419 18.3445 20.0994 18.4169 20.179 18.4603C20.2586 18.5037 20.3537 18.5145 20.4433 18.4905C20.533 18.4665 20.6099 18.4095 20.6571 18.3322C20.7043 18.2548 20.718 18.1633 20.6951 18.078Z"
                      fill="#6F767E"
                    />
                  </svg>
                  <div className="d-flex flex-column">
                    <p>Incluir benefício</p>
                    <span>Acesse a carteira do cliente</span>
                  </div>
                </div>
                <i className="fal fa-chevron-right" />
              </div>
            </If>
            <If cond={!!orderGift}>
              <div
                className={styles.selectedCoupon}
                onClick={() => setOpenCoupons(true)}
              >
                <div className="d-flex align-center gap-15">
                  <If cond={!orderGift?.amount}>
                    <svg
                      width="34"
                      height="34"
                      viewBox="0 0 34 34"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0 16C0 8.45753 0 4.68629 2.34315 2.34315C4.68629 0 8.45753 0 16 0H18C25.5425 0 29.3137 0 31.6569 2.34315C34 4.68629 34 8.45753 34 16V18C34 25.5425 34 29.3137 31.6569 31.6569C29.3137 34 25.5425 34 18 34H16C8.45753 34 4.68629 34 2.34315 31.6569C0 29.3137 0 25.5425 0 18V16Z"
                        fill="#6161FF"
                        fillOpacity="0.15"
                      />
                      <path
                        d="M10.0212 16.1067C9.9258 15.7505 10.1508 15.3818 10.5248 15.2816L21.3432 12.3828C21.7172 12.2826 22.0965 12.4894 22.1919 12.8456L22.4594 13.8437C21.728 14.1993 21.3211 15.005 21.5298 15.7838C21.7385 16.5625 22.4937 17.0568 23.3048 16.9991L23.5723 17.9972C23.6677 18.3534 23.4427 18.7221 23.0687 18.8223L12.2503 21.7211C11.8763 21.8213 11.497 21.6145 11.4016 21.2583L10.0212 16.1067ZM10.3523 14.6377C9.60642 14.8375 9.15475 15.5775 9.34509 16.2879L10.7255 21.4395C10.9158 22.1499 11.677 22.5649 12.4228 22.3651L23.2412 19.4663C23.9871 19.2664 24.4388 18.5264 24.2484 17.816L23.8904 16.4798C23.8473 16.3189 23.648 16.2515 23.479 16.2967C22.919 16.4468 22.3488 16.1359 22.2059 15.6026C22.0631 15.0693 22.4014 14.5149 22.9613 14.3649C23.1304 14.3196 23.2692 14.1616 23.2261 14.0006L22.8681 12.6644C22.6777 11.9541 21.9165 11.539 21.1707 11.7389L10.3523 14.6377ZM19.6599 14.2143C19.637 14.1289 19.5794 14.0565 19.4998 14.0131C19.4203 13.9697 19.3252 13.9588 19.2355 13.9829C19.1458 14.0069 19.0689 14.0639 19.0217 14.1412C18.9745 14.2186 18.9608 14.31 18.9837 14.3954C19.0066 14.4808 19.0642 14.5532 19.1437 14.5966C19.2233 14.64 19.3184 14.6508 19.4081 14.6268C19.4977 14.6028 19.5746 14.5458 19.6218 14.4685C19.6691 14.3911 19.6827 14.2996 19.6599 14.2143ZM20.005 15.5022C19.9821 15.4168 19.9245 15.3444 19.8449 15.301C19.7653 15.2576 19.6703 15.2467 19.5806 15.2708C19.4909 15.2948 19.414 15.3518 19.3668 15.4291C19.3196 15.5065 19.3059 15.5979 19.3288 15.6833C19.3517 15.7687 19.4092 15.8411 19.4888 15.8845C19.5684 15.9279 19.6635 15.9387 19.7531 15.9147C19.8428 15.8907 19.9197 15.8337 19.9669 15.7564C20.0142 15.679 20.0278 15.5875 20.005 15.5022ZM20.0982 17.2026C20.1879 17.1786 20.2648 17.1216 20.312 17.0443C20.3592 16.9669 20.3729 16.8754 20.35 16.7901C20.3272 16.7047 20.2696 16.6323 20.19 16.5889C20.1104 16.5455 20.0154 16.5346 19.9257 16.5587C19.836 16.5827 19.7591 16.6397 19.7119 16.717C19.6647 16.7944 19.651 16.8858 19.6739 16.9712C19.6968 17.0566 19.7543 17.129 19.8339 17.1724C19.9135 17.2158 20.0086 17.2266 20.0982 17.2026ZM20.6951 18.078C20.6723 17.9926 20.6147 17.9202 20.5351 17.8768C20.4555 17.8334 20.3605 17.8225 20.2708 17.8466C20.1811 17.8706 20.1042 17.9276 20.057 18.0049C20.0098 18.0823 19.9961 18.1737 20.019 18.2591C20.0419 18.3445 20.0994 18.4169 20.179 18.4603C20.2586 18.5037 20.3537 18.5145 20.4433 18.4905C20.533 18.4665 20.6099 18.4095 20.6571 18.3322C20.7043 18.2548 20.718 18.1633 20.6951 18.078Z"
                        fill="#4C4CFF"
                      />
                      <path
                        d="M10.0212 16.1067C9.9258 15.7505 10.1508 15.3818 10.5248 15.2816L21.3432 12.3828C21.7172 12.2826 22.0965 12.4894 22.1919 12.8456L22.4594 13.8437C21.728 14.1993 21.3211 15.005 21.5298 15.7838C21.7385 16.5625 22.4937 17.0568 23.3048 16.9991L23.5723 17.9972C23.6677 18.3534 23.4427 18.7221 23.0687 18.8223L12.2503 21.7211C11.8763 21.8213 11.497 21.6145 11.4016 21.2583L10.0212 16.1067ZM10.3523 14.6377C9.60642 14.8375 9.15475 15.5775 9.34509 16.2879L10.7255 21.4395C10.9158 22.1499 11.677 22.5649 12.4228 22.3651L23.2412 19.4663C23.9871 19.2664 24.4388 18.5264 24.2484 17.816L23.8904 16.4798C23.8473 16.3189 23.648 16.2515 23.479 16.2967C22.919 16.4468 22.3488 16.1359 22.2059 15.6026C22.0631 15.0693 22.4014 14.5149 22.9613 14.3649C23.1304 14.3196 23.2692 14.1616 23.2261 14.0006L22.8681 12.6644C22.6777 11.9541 21.9165 11.539 21.1707 11.7389L10.3523 14.6377ZM19.6599 14.2143C19.637 14.1289 19.5794 14.0565 19.4998 14.0131C19.4203 13.9697 19.3252 13.9588 19.2355 13.9829C19.1458 14.0069 19.0689 14.0639 19.0217 14.1412C18.9745 14.2186 18.9608 14.31 18.9837 14.3954C19.0066 14.4808 19.0642 14.5532 19.1437 14.5966C19.2233 14.64 19.3184 14.6508 19.4081 14.6268C19.4977 14.6028 19.5746 14.5458 19.6218 14.4685C19.6691 14.3911 19.6827 14.2996 19.6599 14.2143ZM20.005 15.5022C19.9821 15.4168 19.9245 15.3444 19.8449 15.301C19.7653 15.2576 19.6703 15.2467 19.5806 15.2708C19.4909 15.2948 19.414 15.3518 19.3668 15.4291C19.3196 15.5065 19.3059 15.5979 19.3288 15.6833C19.3517 15.7687 19.4092 15.8411 19.4888 15.8845C19.5684 15.9279 19.6635 15.9387 19.7531 15.9147C19.8428 15.8907 19.9197 15.8337 19.9669 15.7564C20.0142 15.679 20.0278 15.5875 20.005 15.5022ZM20.0982 17.2026C20.1879 17.1786 20.2648 17.1216 20.312 17.0443C20.3592 16.9669 20.3729 16.8754 20.35 16.7901C20.3272 16.7047 20.2696 16.6323 20.19 16.5889C20.1104 16.5455 20.0154 16.5346 19.9257 16.5587C19.836 16.5827 19.7591 16.6397 19.7119 16.717C19.6647 16.7944 19.651 16.8858 19.6739 16.9712C19.6968 17.0566 19.7543 17.129 19.8339 17.1724C19.9135 17.2158 20.0086 17.2266 20.0982 17.2026ZM20.6951 18.078C20.6723 17.9926 20.6147 17.9202 20.5351 17.8768C20.4555 17.8334 20.3605 17.8225 20.2708 17.8466C20.1811 17.8706 20.1042 17.9276 20.057 18.0049C20.0098 18.0823 19.9961 18.1737 20.019 18.2591C20.0419 18.3445 20.0994 18.4169 20.179 18.4603C20.2586 18.5037 20.3537 18.5145 20.4433 18.4905C20.533 18.4665 20.6099 18.4095 20.6571 18.3322C20.7043 18.2548 20.718 18.1633 20.6951 18.078Z"
                        fill="white"
                        fillOpacity="0.2"
                      />
                    </svg>

                    <p>{orderGift?.description || orderGift?.code}</p>
                  </If>
                  <If cond={!!orderGift?.amount}>
                    <i className="fal fa-hand-holding-dollar" />
                    <p>Uso de cashback</p>
                  </If>
                </div>
                <Button design="simple-2">Trocar</Button>
              </div>
            </If>
          </div>

          <Hairline className="mt-20 mb-20" />

          <div className={styles.paymentContainer}>
            <p className={styles.label}>Forma de pagamento</p>

            <Select
              placeholder="Selecione uma forma de pagamento"
              options={[
                ...paymentMethods.map((method) => {
                  return {
                    value: method.alias,
                    label: method.name,
                  };
                }),
              ]}
              value={selectedPayment?.alias}
              onChange={(e) => {
                const method = paymentMethods.find(
                  (m) => m.alias === e.target.value,
                );

                setSelectedPayment(method);

                if (method?.alias === Method.DINHEIRO?.alias) {
                  setChangeAmount(0);
                  setOpenChangeAmount(true);
                } else {
                  setChangeAmount(null);
                  OrderStorage.setChangeFor(null);
                }
              }}
            />

            <If
              cond={
                order?.payment?.payers[0]?.changeFor !== null &&
                selectedPayment?.alias === Method.DINHEIRO?.alias
              }
            >
              <div
                className={styles.changeFor}
                onClick={() => setOpenChangeAmount(true)}
              >
                <div className="d-flex flex-column">
                  <p>Valor para troco</p>
                  <div className="d-flex align-center gap-10">
                    <If cond={order?.payment?.payers[0]?.changeFor > 0}>
                      <span>
                        {Currency.formatCurrency(
                          order?.payment?.payers[0]?.changeFor,
                        )}
                      </span>
                    </If>
                    <If cond={order?.payment?.payers[0]?.changeFor === 0}>
                      <span>Não precisa de troco</span>
                    </If>
                  </div>
                </div>
                <i className="fal fa-pen" />
              </div>
            </If>
          </div>

          <div className={styles.totals}>
            <div className="d-flex align-center justify-between">
              <p className={styles.label}>Valor do pedido</p>
              <p className={styles.value}>
                {Currency.formatCurrency(order?.cart?.total)}
              </p>
            </div>

            <div className="d-flex align-center justify-between">
              <p className={styles.label}>Taxa de entrega</p>
              <p className={styles.value}>
                {isFreeDelivery
                  ? "Grátis"
                  : Currency.formatCurrency(order?.delivery?.total)}
              </p>
            </div>

            <If cond={orderGift}>
              <div className="d-flex align-center justify-between">
                <p className={styles.label}>Cupom</p>
                <p className={styles.couponValue}>
                  {orderGift &&
                    orderGift.type &&
                    order.gift &&
                    ![
                      "CASHBACK_ACCUMULATE",
                      "CASHBACK",
                      "FREE_DELIVERY",
                    ].includes(orderGift.type) && (
                      <div className="d-flex align-center justify-between">
                        <div className="d-flex align-center gap-5">
                          <span className={styles.giftCode}>
                            {getGiftType(orderGift)}
                          </span>
                          <span className={styles.giftValue}>
                            {Currency.formatCurrency(
                              order.gift?.total ||
                                (orderGift?.amount > order?.total
                                  ? order?.total
                                  : orderGift?.amount) ||
                                0,
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                </p>
              </div>
            </If>

            <div className="d-flex align-center justify-between">
              <p className={styles.totalLabel}>VALOR TOTAL</p>
              <p className={styles.totalValue}>
                {Currency.formatCurrency(order?.total)}
              </p>
            </div>

            <Button
              title="Confirmar pedido"
              design="primary"
              onClick={onCheckout}
              className="w-100 mt-25"
              disabled={
                !selectedPayment ||
                !selectedCustomer ||
                !OrderStorage.getDelivery()?.address ||
                loading
              }
              loading={loading}
            >
              Finalizar
            </Button>
          </div>
        </div>
      </Drawer>

      <Drawer
        direction="bottom"
        title={`Troco para ${Currency.formatCurrency(order?.total)}`}
        visible={openChangeAmount}
        showCloseButton={false}
      >
        <CurrencyInput
          label="Valor"
          value={changeAmount}
          onChange={(e, m, f) => setChangeAmount(f)}
          autoFocus
        />

        <Button
          title="Confirmar"
          design="primary"
          onClick={() => {
            setOpenChangeAmount(false);
            setSelectedPayment({
              ...selectedPayment,
              changeAmount,
            });
            OrderStorage.setChangeFor(changeAmount);
          }}
          className="w-100 mt-25"
          disabled={
            !changeAmount || changeAmount <= 0 || changeAmount < order?.total
          }
        >
          Confirmar
        </Button>

        <Button
          onClick={() => {
            setOpenChangeAmount(false);
            setSelectedPayment({
              ...selectedPayment,
              changeAmount: 0,
            });
            OrderStorage.setChangeFor(0);
          }}
          className="w-100 mt-10"
        >
          Não precisa de troco
        </Button>
      </Drawer>

      <Drawer
        visible={openCoupons}
        onClose={() => setOpenCoupons(false)}
        title="Carteira do cliente"
        showBackButton
        backButton={() => setOpenCoupons(false)}
      >
        <If cond={loadingCoupons}>
          <div className="d-flex align-center justify-center h-100">
            <Loading />
          </div>
        </If>
        <If cond={!loadingCoupons}>
          <div className={styles.benefitsContainer}>
            <div className="d-flex align-center mb-10 w-100 justify-between">
              <Label className="mb-0">
                Selecione o cupom ({coupons.length})
              </Label>
              <Button
                design="default-icon-mini-2"
                onClick={() => getCustomerInfos()}
              >
                <i className="far fa-rotate-right" />
              </Button>
            </div>

            <div className={styles.cashback}>
              <p>Saldo de {programByPoint ? "pontos" : "cashback"}</p>
              <span>
                {programByPoint
                  ? `${cashback} pts`
                  : Currency.formatCurrency(cashback)}
              </span>
            </div>

            <If cond={cashback > 0 && !programByPoint}>
              <Button
                design={
                  selectedCoupon?.type === "CASHBACK_CONSUME"
                    ? "primary-outline"
                    : "primary-2"
                }
                className="w-100 mt-20"
                onClick={() => {
                  if (cashback <= 0) return;

                  if (customerBlocked) {
                    alert({
                      text: "Cliente bloqueado para uso de cashback.",
                    });
                    return;
                  }

                  if (selectedCoupon?.type === "CASHBACK_CONSUME") {
                    setSelectedCoupon(null);
                    OrderStorage.setGift(null);
                    return;
                  }

                  setSelectedCoupon({
                    type: "CASHBACK_CONSUME",
                    amount: cashback,
                  });
                }}
              >
                {selectedCoupon?.type === "CASHBACK_CONSUME"
                  ? "Cancelar uso de saldo"
                  : "Usar saldo de cashback"}
              </Button>
            </If>

            <InputSearch
              placeholder="Buscar por..."
              value={searchCoupon}
              design="two"
              className="mt-20"
              onChange={(e) => setSearchCoupon(e.target.value)}
            />

            <Add
              text="Novo cupom"
              className="d-flex flex-column mt-20"
              onClick={() => setOpenAddCoupon(true)}
            />

            <If cond={filteredCoupons.length > 0}>
              <Hairline className="mt-20 mb-20" />

              <div className={styles.coupons}>
                {filteredCoupons.map((coupon) => {
                  const rules = Benefit.getRules(coupon);
                  const canBeUsed = Benefit.canBeUsed(coupon);

                  const title = Benefit.getCouponTitle(coupon, programByPoint);

                  const isSelected = selectedCoupon?.coupon === coupon?.coupon;
                  coupon.rules = rules;

                  return (
                    <div
                      className={css(
                        styles.coupon,
                        !canBeUsed && styles.disabled,
                        isSelected && styles.selected,
                      )}
                      key={coupon.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!canBeUsed) return;

                        if (selectedCoupon?.id === coupon.id) {
                          setSelectedCoupon(null);
                          return;
                        }

                        setSelectedCoupon(coupon);
                      }}
                    >
                      <div className="d-flex align-center">
                        <Radio
                          name="coupon"
                          value={true}
                          checked={isSelected}
                          onChange={() => setSelectedCoupon(coupon)}
                          readOnly
                        />
                        <div className={styles.divider} />
                        <div className={styles.image}>
                          {coupon.image && (
                            <img
                              src={
                                process.env.REACT_APP_ASSETS_URL +
                                `/` +
                                coupon.image
                              }
                              alt=""
                            />
                          )}
                        </div>
                        <div className="d-flex flex-column">
                          <p className={styles.name}>
                            {coupon.description || coupon.code}
                          </p>
                          <span className={styles.title}>{title}</span>
                        </div>
                      </div>
                      <p className={styles.rules}>
                        {(coupon?.rules || []).map((rule, index) => (
                          <span className={styles.rule} key={index}>
                            {rule}
                          </span>
                        ))}
                      </p>
                    </div>
                  );
                })}
              </div>
            </If>

            <div className={styles.footer}>
              <Button
                design="primary"
                onClick={() => {
                  if (!selectedCoupon) {
                    OrderStorage.setGift(null);
                    setOpenCoupons(false);
                    return;
                  }

                  const parsedGift = Benefit.parseCouponToOrderGift(
                    selectedCoupon,
                    order.cart?.catalog?.items || [],
                  );
                  OrderStorage.setGift(parsedGift);
                  setOpenCoupons(false);
                }}
              >
                Salvar
              </Button>
            </div>
          </div>
        </If>
      </Drawer>

      <AddCoupon
        visible={openAddCoupon}
        onClose={() => setOpenAddCoupon(false)}
        onAdd={() => {
          setOpenAddCoupon(false);
          getCustomerInfos();
        }}
        customerId={customerId}
      />

      <Drawer
        visible={openOrderSuccess}
        onClose={() => setOpenOrderSuccess(false)}
        title="Pedido realizado"
        subtitle="O pedido do cliente já foi encaminhado para a tela de pedidos."
        withoutTransition
      >
        <div className={styles.successOrder}>
          <div className={styles.successIcon}>
            <SuccessIcon />
          </div>

          <div className={styles.footer}>
            <Button
              design="primary"
              onClick={() => {
                setOpenOrderSuccess(false);
                onClose();
              }}
            >
              Finalizar
            </Button>
            <Button
              design="default-3"
              onClick={() => navigate("/pedidos")}
              className="mt-10"
            >
              Ir para pedidos
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default ConfirmOrder;
